/**
 * Reading a lineage row: its agreed value state, its one-line source, whose
 * statement it is. Plain functions, kept apart from the components that
 * render them so every surface — the Audit rows, the metric bar, the
 * Overview — reads a figure the same way.
 */
import type { LoanFieldLineageField, StatedAttribution } from '@/pages/pipeline-intelligence/types'
import type { DossierCoverage } from '@/services/ingestion'
import type { SponsorFigure } from '@/services/dealFile'
import { toFieldState, type FieldState } from './fieldState'

/** What a Confirmed figure saved without a reason says in place of one
 *  (nd3-05; whether to require a reason is owner decision #8). */
export const NO_RATIONALE = 'no rationale recorded'

/** "Confirmed by <who>[ on <date>] — <why>", or "· no rationale recorded"
 *  when the person gave none: Confirmed promises who, when and why, so a
 *  missing why is said rather than left for the reader to assume. */
export function confirmedText(
  c: { by?: string | null; rationale?: string | null } | null | undefined,
  when = '',
): string {
  const why = (c?.rationale ?? '').trim()
  return `Confirmed by ${c?.by ?? 'a reviewer'}${when}${why ? ` — ${why}` : ` · ${NO_RATIONALE}`}`
}

/** The four states a present figure can be in. */
export const VALUE_STATES: readonly FieldState[] = ['stated', 'derived', 'suggested', 'confirmed']

/** The display state of one lineage row: the API's agreed `state` when it
 *  sends one, the raw engine word otherwise. The raw word alone cannot tell a
 *  person's approval from a machine backfill, so `state` always wins. */
export function stateOfLineage(
  field: { state?: string | null; valueState?: string | null } | null | undefined,
  fallback: FieldState = 'missing',
): FieldState {
  if (!field) return fallback
  if (field.state) return toFieldState(field.state)
  return toFieldState(field.valueState ?? fallback)
}

const ATTRIBUTION_TEXT: Record<StatedAttribution, string> = {
  sponsor: "The sponsor's underwriting model",
  broker: 'The broker',
  external: 'A party outside the firm',
  client: 'This firm',
  unattributed: 'Not established',
}

export function attributionText(value: string | null | undefined): string | null {
  if (!value) return null
  return ATTRIBUTION_TEXT[value as StatedAttribution] ?? value
}

/** A timestamp a reader can place, or the raw text if it will not parse. */
export function stampText(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString()
}

/** The one-line source a table cell shows for a lineage row. */
export function lineageSummary(field: LoanFieldLineageField | null | undefined): string {
  if (!field) return ''
  return field.summary ?? field.source ?? ''
}

/**
 * The one display state of a catalogue row.
 *
 * The lineage's agreed state for a value the deal holds; the intake record's
 * state for a value only the overlay supplies; and a present value nothing
 * accounts for is a SUGGESTION — never "extracted · Not recorded".
 */
export function catalogueState(
  lineage: LoanFieldLineageField | undefined,
  empty: boolean,
  intake?: LoanFieldLineageField,
): FieldState {
  const own = stateOfLineage(lineage, empty ? 'missing' : 'suggested')
  if (empty) {
    return own === 'not_applicable' || own === 'conflicted' || own === 'in_unread_model'
      || own === 'in_unread_attachment' ? own : 'missing'
  }
  if (own === 'missing' || own === 'not_applicable' || own === 'in_unread_model'
    || own === 'in_unread_attachment') {
    return intake ? stateOfLineage(intake, 'suggested') : 'suggested'
  }
  return own
}

/**
 * Is `value` the deal column's own marker for "nothing stated"?
 *
 * `loans.loan_amount` is NOT NULL, so promotion stores 0.0 where a package
 * states no loan amount, and the lineage reports that value as `missing`. Read
 * as a figure, it printed "$0 · Suggested" on Cranbury's Audit while the header
 * beside it said "—". Only the lineage's own held value is read this way: a
 * true absence carries no value, and an overlay value is left alone.
 */
export function heldAsAbsent(lineage: LoanFieldLineageField | undefined, value: unknown): boolean {
  if (!lineage || stateOfLineage(lineage) !== 'missing') return false
  const held = lineage.currentValue
  if (held === null || held === undefined || held === '') return false
  if (value === null || value === undefined || value === '') return false
  const a = Number(held)
  const b = Number(value)
  return Number.isFinite(a) && Number.isFinite(b) ? a === b : String(held) === String(value)
}

/**
 * The figure a lineage row holds, for a surface that prints the row itself
 * (the Inspector's lineage cards, the printed dossier's lineage table).
 *
 * A row the lineage calls missing or not applicable holds no figure, whatever
 * the column stores: Cranbury's NOT NULL `loan_amount` 0.0 printed "0" beside
 * "Missing" in the Inspector and "0 · Suggested" in the printed dossier.
 */
export function heldValue(field: LoanFieldLineageField | null | undefined): unknown {
  if (!field) return null
  const state = stateOfLineage(field, 'suggested')
  if (state === 'missing' || state === 'not_applicable') return null
  return field.currentValue
}

/** Intake coverage rows, in the lineage's shape, for the overlay's values. */
export function intakeLineageOf(
  coverage: DossierCoverage | undefined,
): Map<string, LoanFieldLineageField> {
  const map = new Map<string, LoanFieldLineageField>()
  for (const group of coverage?.groups ?? []) {
    for (const f of group.fields) {
      if (f.value === null || f.value === undefined || f.value === '') continue
      if (f.status !== 'stated' && f.status !== 'derived' && f.status !== 'suggested' && f.status !== 'confirmed') continue
      const prov = f.provenance
      const where = [prov?.sourceDoc, prov?.cellRef, prov?.lineNo != null ? `line ${prov.lineNo}` : null]
        .filter(Boolean)
        .join(' · ')
      map.set(f.field, {
        fieldName: f.field,
        currentValue: f.value,
        valueState: f.status,
        state: f.status,
        summary:
          f.status === 'stated'
            ? `On the intake record — ${where || 'the package'}`
            : f.status === 'confirmed'
              ? confirmedText(f.confirmation)
              : f.status === 'derived'
              ? `Derived on the intake record: ${f.formula ?? ''}`
              : `Suggested: ${f.basis ?? f.note ?? 'a platform default'}`,
        provenance:
          f.status === 'stated'
            ? {
                document: prov?.sourceDoc ?? null,
                cellRef: prov?.cellRef ?? null,
                lineNo: prov?.lineNo ?? null,
                method: prov?.method ?? null,
                inheritedFrom: prov?.inheritedFrom ?? null,
              }
            : null,
        derivation:
          f.status === 'derived'
            ? {
                formula: f.formula ?? null,
                inputs: (f.inputs ?? []).map((i) => ({ field: i.field, label: i.label, value: i.value })),
                inputsRecorded: true,
                engineVersion: null,
                computedAt: null,
              }
            : null,
        suggestion: f.status === 'suggested' ? { basis: f.basis ?? f.note ?? '' } : null,
        confirmation:
          f.status === 'confirmed'
            ? {
                by: f.confirmation?.by ?? null,
                at: f.confirmation?.at ?? null,
                rationale: f.confirmation?.rationale ?? null,
                via: f.confirmation?.via ?? null,
                kind: f.confirmation?.kind ?? null,
              }
            : null,
        variance: f.variance
          ? { stated: f.variance.stated, computed: f.variance.computed, formula: f.variance.formula ?? null }
          : null,
        source: null,
        confidence: null,
        editorEmail: null,
        updatedAt: null,
        history: [],
      })
    }
  }
  return map
}

/**
 * Keep the SPONSOR tag only where the sponsor's own document is the source.
 *
 * A page tags a tile "Sponsor" when the figure on screen EQUALS the sponsor
 * model's cell. That coincidence hid a value a person confirmed from the
 * countersigned term sheet (Calloway's loan amount) and put the sponsor's
 * name on a broker's figure (ECON-24, OVR-25). The lineage knows whose
 * statement a value is, so it decides; with no lineage for the field the
 * page's own finding stands.
 */
export function sponsorAttributionHolds(
  lineage: LoanFieldLineageField | null | undefined,
  sponsor: SponsorFigure | undefined,
): boolean {
  if (!sponsor) return false
  if (!lineage || !lineage.state) return true
  const state = stateOfLineage(lineage)
  if (state !== 'stated') return false
  return lineage.provenance?.attribution === 'sponsor'
}

